import { List, TextField, NumberField, FunctionField, useDataProvider, useListContext, sanitizeListRestProps, TopToolbar } from 'react-admin';
import Datagrid from '../Commons/Datagrid';
import PostPagination from '../Commons/PostPagination';
import Button from '@mui/material/Button';
import * as XLSX from 'xlsx';
import { useMediaQuery, createTheme, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import historyTracker from '../Commons/historyTracker';
import AuthNotification from '../Commons/AuthNotification';
import { StyleTheme, Style } from '../Commons/Styles.jsx';
import { createStyles, makeStyles } from '@mui/styles';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// Mismo catalogo fijo que InventoryIntegrationCreate.jsx (asistelibros aca es BA/BE/SA/SE,
// distinto del dominio de Gastos Contables -- BA/SA/IP -- ver comentario en ese archivo).
const BooksFlagNames = {
  BA: 'Bienes Afectos',
  BE: 'Bienes Exentos',
  SA: 'Servicios Afectos',
  SE: 'Servicios Exentos',
};

// Mismo patron que AccountingExpenseListActions en AccountingExpensesList.jsx (y que
// InvoiceListActions en ProviderInvoicesList.jsx): un TopToolbar propio que navega con
// history.push (state + query) para llevar la factura (corre_cbfactura) al formulario de
// creacion, y que reimplementa "Exportar" con la misma logica que Commons/ListActions.jsx
// porque este TopToolbar propio reemplaza por completo al compartido, no lo envuelve.
const InventoryIntegrationListActions = (props) => {
  const { create, onCreate, download, ...rest } = props;
  const { sort, resource, filterValues } = useListContext();
  const dataProvider = useDataProvider();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  async function exportToExcel(filename, resource, sort, filterValues) {
    let data = await dataProvider.getList(resource, { pagination: { page: 1, perPage: 1000 }, sort, filter: filterValues });
    let columns = [];
    for (let key in data.data[0]) {
      columns.push(key)
    }
    const ws = XLSX.utils.json_to_sheet(data.data, { header: columns });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  return (
    <TopToolbar {...sanitizeListRestProps(rest)}>
      {!isSmall && download ? (
        <Button
          onClick={() => exportToExcel('data', resource, sort, filterValues)}
          style={Style.ToolBarButton}>
          Exportar
        </Button>
      ) : null}
      {create ? (
        <Button onClick={onCreate} color="primary" style={Style.ToolBarButton}>
          Crear
        </Button>
      ) : null}
    </TopToolbar>
  );
};

// El filtro ya viaja codificado en la URL como ?filter={"invoice":"..."} (asi es como se
// abre esta pantalla desde el boton "Integración de Inventario" de ProviderInvoiceShow),
// asi que se reutiliza como fuente de verdad para saber a que factura esta acotado el
// listado. IMPORTANTE: no se puede confiar en location.state aqui, por la misma razon
// documentada en ProviderInvoicesList.jsx (<List syncWithLocation> reescribe la URL apenas
// monta y esa reescritura pierde el location.state original de history.push).
const getInvoiceFromFilterQuery = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    const filterQuery = params.get('filter');
    if (!filterQuery) return null;
    const parsed = JSON.parse(filterQuery);
    return parsed?.invoice ?? null;
  } catch {
    return null;
  }
};

const getInvoiceNumberFromQuery = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    return params.get('invoiceNumber') || null;
  } catch {
    return null;
  }
};

// Mismo motivo que AccountingExpensesEmpty en AccountingExpensesList.jsx: se prefiere
// seguir mostrando el encabezado de columnas normal aunque no haya integraciones de
// inventario registradas, en vez del mensaje generico de react-admin.
const InventoryIntegrationsEmpty = () => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell style={Style.DataGrid}>Artículo</TableCell>
        <TableCell style={Style.DataGrid}>Almacén</TableCell>
        <TableCell style={Style.DataGrid}>Razón</TableCell>
        <TableCell style={Style.DataGrid}>Cantidad</TableCell>
        <TableCell style={Style.DataGrid}>Monto</TableCell>
        <TableCell style={Style.DataGrid}>Clasificación</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell colSpan={6} style={{ textAlign: 'center' }}>
          No hay integraciones de inventario registradas para esta factura.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

// Listado de integraciones de inventario de una factura puntual (sp_lista_cbfacturainteginv
// acotado por corre_cbfactura). Se llega aqui desde el boton "Integración de Inventario" de
// ProviderInvoiceShow. A diferencia de AccountingExpensesList, el id de cada fila es
// directamente el correlativo (sp_get_cbfacturainteginv no necesita corre_cbfactura para
// ubicar la fila), asi que no hace falta armar un id compuesto.
// El breadcrumb usa una etiqueta fija ("Integración de Inventario"): al ser un listado, no
// un registro puntual, no tiene sentido sufijarlo con el numero de factura. invoiceNumber/
// invoiceId se mantienen igual, ya que los sigue necesitando handleCreateIntegration para
// precargar el formulario de creacion.
const useStyles = makeStyles(() => createStyles(StyleTheme));

const InventoryIntegrationsList = (props) => {
  const classes = useStyles();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openAuth, setOpenAuth] = useState(false);
  const [auth, setAuth] = useState(false);
  const [authCreate, setAuthCreate] = useState(false);
  // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
  // "initialLoading" en el return): esta pantalla necesita traer permisos antes de poder
  // mostrar el listado, y sin este estado la ventana se veia en blanco (solo el
  // breadcrumb) durante esa espera.
  const [initialLoading, setInitialLoading] = useState(true);
  const dataProvider = useDataProvider();
  const invoiceNumber = getInvoiceNumberFromQuery(location.search) || location.state?.invoiceNumber;
  const invoiceId = getInvoiceFromFilterQuery(location.search) || location.state?.invoiceId;

  useEffect(() => {
    (async () => {
      try {
        const currentPath = location.pathname;
        // "Inventarios" (plural) para diferenciarlo en el breadcrumb del nivel de detalle
        // (InventoryIntegrationShow.jsx), que usa el mismo texto en singular -- sin esto
        // el breadcrumb mostraba "Integración de Inventario > Integración de Inventario"
        // dos veces seguidas al entrar a una integracion puntual.
        historyTracker.addPath(currentPath, 'Integración de Inventarios', 6);

        const authdata = { data: hasPermissions('InventoryIntegration', 'list,create') };
        if (authdata.data.length > 0) {
          setAuth(authdata.data[0]);
          setOpenAuth(!authdata.data[0]);
          setAuthCreate(authdata.data[1]);
        } else {
          setAuth(false);
          setOpenAuth(true)
        }
      }
      catch {
      }
      finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, location.pathname]);

  const handleCreateIntegration = () => {
    navigate({
      pathname: '/provider/inventoryintegration/create',
      search: `?invoice=${encodeURIComponent(invoiceId)}&invoiceNumber=${encodeURIComponent(invoiceNumber || '')}`,
    }, { state: { invoiceNumber, invoiceId } });
  };

  const handleRedirectInvoice = () => {
    navigate(`/provider/invoice/${invoiceId}/show`);
  };

  const handleRedirectProviderList = () => {
    navigate(`/provider`);
  };

  const [openProviders, setOpenProviders] = useState(true);

  const handleClickOpenProviders = () => {
    setOpenProviders(!openProviders);
  };

  const menus = [{
    icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
    open: openProviders,
    openAction: handleClickOpenProviders,
    actions: [
      { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
      { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Factura', onClick: handleRedirectInvoice },
    ]
  }];

  let pagination = <PostPagination menu={<InternalMenu menus={menus} />} />;

  return (
    <div class="container">
      <BreadCrumbCustom history={historyTracker.getHistory()} />
      {initialLoading ? (
        // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y el
        // mismo CircularProgress que se usa en otras pantallas para la espera de guardado,
        // pero aqui cubre la espera de auth, que antes dejaba la pantalla en blanco.
        <div class="Loading" style={{ display: 'block' }}>
          <CircularProgress size="15rem" />
        </div>
      ) : auth ? (
        <div class="row">
          <div class="col-12">
            {isSmall ? (
              <List empty={false} {...props} actions={<InventoryIntegrationListActions create={authCreate} onCreate={handleCreateIntegration} />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props}>
                </Datagrid>
              </List>
            ) : (
              <List empty={false} perPage={10} {...props} actions={<InventoryIntegrationListActions create={authCreate} onCreate={handleCreateIntegration} download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} rowClick="show" style={Style.DataGrid} empty={<InventoryIntegrationsEmpty />}>
                  <TextField source="articleName" style={Style.fieldList} label="Artículo" cellClassName={classes.fields} />
                  <TextField source="warehouseName" style={Style.fieldList} label="Almacén" cellClassName={classes.fields} />
                  <TextField source="reasonName" style={Style.fieldList} label="Razón" cellClassName={classes.fields} />
                  <NumberField source="quantity" style={Style.fieldList} label="Cantidad" cellClassName={classes.fields} />
                  <NumberField source="amount" style={Style.fieldList} label="Monto" cellClassName={classes.fields} options={{ style: 'currency', currency: 'GTQ' }} />
                  <FunctionField source="booksFlag" style={Style.fieldList} label="Clasificación" cellClassName={classes.fields} render={record => BooksFlagNames[record.booksFlag] || record.booksFlag} />
                </Datagrid>
              </List>
            )}
          </div>
        </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)}
    </div>
  )
};

export default InventoryIntegrationsList;
